import React from 'react';
import { SectionCard } from '../../layout/memberPrimitives';
import type { QuarterProgress, QuarterStepState } from '../../../utils/dashboard/quarterStepper';

const STATE_STYLE: Record<QuarterStepState, { dot: string; label: string; ring: string }> = {
  done: { dot: 'var(--melio-olive-deep)', label: 'var(--melio-ink)', ring: 'var(--melio-olive-deep)' },
  active: { dot: 'var(--melio-terracotta-deep)', label: 'var(--melio-ink)', ring: 'var(--melio-terracotta-deep)' },
  upcoming: { dot: 'transparent', label: 'var(--melio-text-muted)', ring: 'var(--melio-border)' },
  unknown: { dot: 'transparent', label: 'var(--melio-text-muted)', ring: 'var(--melio-border)' },
};

/**
 * Where the member is in the quarterly cycle.
 *
 * A step reading 'unknown' means its evidence could not be loaded — it renders
 * as a neutral dash rather than as work still to do, because a denied read is
 * not the same as an outstanding task.
 */
export default function QuarterStepper({ progress }: { progress: QuarterProgress }) {
  if (progress.steps.length === 0) return null;

  return (
    <SectionCard eyebrow="Your quarter" title={`${progress.quarterLabel} review`}>
      <ol className="flex flex-wrap gap-x-2 gap-y-4 list-none p-0 m-0">
        {progress.steps.map((step, index) => {
          const style = STATE_STYLE[step.state];
          return (
            <li key={step.id} className="flex items-start gap-2 flex-1" style={{ minWidth: 130 }}>
              <div className="flex flex-col items-center pt-0.5">
                <span
                  className="rounded-full flex items-center justify-center shrink-0"
                  style={{
                    width: 14,
                    height: 14,
                    backgroundColor: style.dot,
                    boxShadow: `inset 0 0 0 1.5px ${style.ring}`,
                  }}
                  aria-hidden="true"
                />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-semibold leading-tight" style={{ color: style.label }}>
                  {step.label}
                </p>
                <p className="text-[11px] leading-snug mt-0.5" style={{ color: 'var(--melio-text-muted)' }}>
                  {step.state === 'unknown' ? '—' : step.detail}
                </p>
              </div>
              {index < progress.steps.length - 1 && (
                <span
                  className="hidden lg:block self-start mt-2 flex-1"
                  style={{ height: 1, backgroundColor: 'var(--melio-border)' }}
                  aria-hidden="true"
                />
              )}
            </li>
          );
        })}
      </ol>
      <p className="text-xs mt-4 pt-3" style={{ color: 'var(--melio-text-muted)', borderTop: '1px solid var(--melio-border)' }}>
        {/* A booked delivery call, so it is stated as a fact rather than an
            estimate. With nothing booked we say who will confirm, and never
            guess a date. */}
        {progress.expectedReportLabel
          ? `Your clinician will talk you through it on ${progress.expectedReportLabel}.`
          : 'Your care team will confirm your report date.'}
      </p>
    </SectionCard>
  );
}
